import React, { useState } from 'react';
import { Container, Row, Col, Button, Alert, Spinner, Collapse, Card } from 'react-bootstrap';
import { uploadDocument, validateDocument } from '../services/api';

function ValidatorPage() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [reasoningOpen, setReasoningOpen] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/plain') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a text (.txt) file');
      setFile(null);
    }
  };

  const handleDragOver = (e) => {
e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/plain') {
 setFile(droppedFile);
      setError(null);
    } else {
      setError('Please drop a text (.txt) file');
 }
  };

  const handleUploadClick = () => {
    document.getElementById('fileInput').click();
  };

  const handleValidate = async () => {
    if (!file) {
   setError('Please select a file first');
      return;
    }

 setLoading(true);
    setError(null);
    setResults(null);

    try {
    // Upload the document
  const uploadResponse = await uploadDocument(file);
      console.log('Upload response:', uploadResponse);
      
      // Validate the document
      const validationResponse = await validateDocument(uploadResponse.documentId);
      console.log('Validation response:', validationResponse);
      
      // Check if we received a valid response
      if (!validationResponse) {
        throw new Error('No validation response received');
      }
      
      setResults(validationResponse);
  } catch (err) {
      console.error('Validation error:', err);
      // Display detailed error information if available
      let errorMessage = err.message || 'An error occurred during validation';
      
      // If the error has response data with detailed error info, show it
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.error) {
          errorMessage = `${errorData.message}: ${errorData.error}`;
          if (errorData.type) {
            errorMessage += ` (${errorData.type})`;
          }
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Parse the validation results to extract sections
  const parseValidationResults = (validationText) => {
    if (!validationText) return null;

    const sections = {
      validation: '',
      sources: [],
      reasoning: ''
    };

    // First, extract reasoning section (appears before "Validation:")
    const reasoningMatch = validationText.match(/Reasoning:([\s\S]*?)Validation:/i);
    if (reasoningMatch) {
      sections.reasoning = reasoningMatch[1].trim();
    }

    // Extract main validation section (starts at "Validation:" and goes to end or "Sources")
    // Look for the validation content after "Validation:" marker
    const validationStartIndex = validationText.indexOf('Validation:');
    if (validationStartIndex !== -1) {
      const afterValidation = validationText.substring(validationStartIndex + 11); // Skip "Validation:"
      
      // Find where sources section starts
      const sourcesIndex = afterValidation.search(/###\s*\*{0,2}Sources\*{0,2}/i);
      
      if (sourcesIndex !== -1) {
        sections.validation = afterValidation.substring(0, sourcesIndex).trim();
        
        // Extract sources from the remaining text
        const sourcesText = afterValidation.substring(sourcesIndex);
        const sourcesMatch = sourcesText.match(/###\s*\*{0,2}Sources\*{0,2}([\s\S]*?)(?:###\s*\*{0,2}Summary|$)/i);
        
        if (sourcesMatch) {
          const sourcesList = sourcesMatch[1].trim();
          // Split by numbered list items (1. 2. 3. etc.)
          const sourceItems = sourcesList.split(/(?:^|\n)\d+\.\s+/).filter(s => s.trim());
          sections.sources = sourceItems.map(s => s.trim());
        }
      } else {
        // No sources found, validation goes to end
        sections.validation = afterValidation.trim();
      }
    }

    return sections;
  };

  // Format markdown-like text for display as JSX elements
  const formatText = (text) => {
    if (!text) return null;
    
    // Replace literal \n strings with actual newlines, and unescape quotes
    let cleanedText = text
      .replace(/\\n/g, '\n')
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'");
    
    const lines = cleanedText.split('\n');
    const elements = [];
    let currentParagraph = [];
    let inList = false;
    
    // Matches text wrapped in double asterisks (e.g., **bold**) and replaces with <strong>.
    const processBoldText = (line) => {
      const parts = [];
      // Regex: matches **text** (non-greedy)
      const boldRegex = /\*\*(.+?)\*\*/g;
      let lastIndex = 0;
      let key = 0;
      
      // Use matchAll for clarity and to avoid regex state issues
      const matches = Array.from(line.matchAll(boldRegex));
      for (const match of matches) {
        if (match.index > lastIndex) {
          parts.push(line.substring(lastIndex, match.index));
        }
        parts.push(<strong key={`bold-${elements.length}-${key++}`}>{match[1]}</strong>);
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < line.length) {
        parts.push(line.substring(lastIndex));
      }
      
      return parts.length > 0 ? parts : line;
    };
    
    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const processedContent = processBoldText(currentParagraph.join(' '));
        elements.push(
          <p key={`p-${elements.length}`} className="mb-3">
            {processedContent}
          </p>
        );
        currentParagraph = [];
      }
    };
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      // Empty line - flush current paragraph
      if (!trimmed) {
        flushParagraph();
        if (inList) {
          inList = false;
        }
        continue;
      }
      
      // Headers
      if (trimmed.startsWith('####')) {
        flushParagraph();
        const headerText = trimmed.replace(/^####\s*\*{0,2}/, '').replace(/\*{0,2}\s*$/, '');
        elements.push(<h5 key={`h5-${elements.length}`} className="mt-4 mb-2 fw-bold">{headerText}</h5>);
      } else if (trimmed.startsWith('###')) {
        flushParagraph();
        const headerText = trimmed.replace(/^###\s*\*{0,2}/, '').replace(/\*{0,2}\s*$/, '');
        elements.push(<h4 key={`h4-${elements.length}`} className="mt-4 mb-3 fw-bold text-primary">{headerText}</h4>);
      }
      // Horizontal rule
      else if (trimmed === '---') {
        flushParagraph();
        elements.push(<hr key={`hr-${elements.length}`} className="my-4" />);
      }
      // Blockquote
      else if (trimmed.startsWith('>')) {
        flushParagraph();
        const quoteText = trimmed.replace(/^>\s*/, '').replace(/\*\*/g, '');
        elements.push(
          <blockquote key={`bq-${elements.length}`} className="border-start border-primary border-3 ps-3 py-2 mb-3 text-muted fst-italic bg-light">
            {quoteText}
          </blockquote>
        );
      }
      // Numbered list item
      else if (trimmed.match(/^\d+\.\s+/)) {
        flushParagraph();
        if (!inList) {
          inList = true;
        }
        const itemText = trimmed.replace(/^\d+\.\s+/, '');
        const processedContent = processBoldText(itemText);
        elements.push(
          <div key={`li-${elements.length}`} className="mb-2 ms-3">
            <span className="me-2">•</span>
            {processedContent}
          </div>
        );
      }
      // Regular text
      else {
        currentParagraph.push(trimmed);
      }
    }
    
    // Flush any remaining paragraph
    flushParagraph();
    
    return <div>{elements}</div>;
  };

  return (
    <div className="validator-container">
 <Container>
        <Row className="mb-4">
  <Col>
            <h1 className="text-center mb-3">Document Validator</h1>
      <p className="text-center text-muted">
      Upload a text document to validate facts and check links for accuracy
   </p>
        </Col>
   </Row>

     {/* Upload Area */}
        <Row className="justify-content-center">
  <Col lg={8}>
          <div 
          className={`upload-area ${dragOver ? 'drag-over' : ''}`}
       onDragOver={handleDragOver}
       onDragLeave={handleDragLeave}
  onDrop={handleDrop}
         onClick={handleUploadClick}
            >
        <div className="upload-icon">??</div>
         <h4>
     {file ? file.name : 'Drag & Drop or Click to Upload'}
     </h4>
       <p className="text-muted mb-3">
        Supported format: Text files (.txt)
              </p>
         <input
      id="fileInput"
     type="file"
       className="file-input"
                accept=".txt"
                onChange={handleFileChange}
     />
     {file && (
  <div className="mt-3">
         <Button 
          variant="primary" 
        size="lg"
 onClick={(e) => {
                 e.stopPropagation();
           handleValidate();
     }}
  disabled={loading}
        >
         {loading ? (
      <>
         <Spinner
           as="span"
animation="border"
           size="sm"
  role="status"
          aria-hidden="true"
          className="me-2"
                />
      Validating...
     </>
        ) : (
            'Validate Document'
           )}
  </Button>
     </div>
 )}
            </div>

   {error && (
            <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
        {error}
              </Alert>
            )}
 </Col>
        </Row>

  {/* Results Display */}
        {results && (() => {
          console.log('Rendering results:', results);
          const validationText = results.validation || results.message || JSON.stringify(results);
          console.log('Validation text to parse:', validationText);
          const parsed = parseValidationResults(validationText);
          
          return (
            <Row className="justify-content-center mt-4">
              <Col lg={10}>
                <Card className="shadow-sm">
                  <Card.Header className="bg-primary text-white">
                    <h3 className="mb-0">Validation Results</h3>
                  </Card.Header>
                  <Card.Body>
                    {parsed ? (
                      <>
                        {/* Main Validation Section */}
                        {parsed.validation && (
                          <div className="mb-4">
                            <div className="validation-content">
                              {formatText(parsed.validation)}
                            </div>
                          </div>
                        )}

                        {/* Sources Section (Collapsible) */}
                        {parsed.sources && parsed.sources.length > 0 && (
                          <div className="mb-3">
                            <Button
                              onClick={() => setSourcesOpen(!sourcesOpen)}
                              aria-controls="sources-collapse"
                              aria-expanded={sourcesOpen}
                              variant="outline-primary"
                              className="w-100 text-start d-flex justify-content-between align-items-center"
                            >
                              <span><strong>📚 Sources Used</strong></span>
                              <span>{sourcesOpen ? '▼' : '▶'}</span>
                            </Button>
                            <Collapse in={sourcesOpen}>
                              <div id="sources-collapse" className="mt-3">
                                <Card className="bg-light">
                                  <Card.Body>
                                    <ol className="mb-0">
                                      {parsed.sources.map((source, index) => (
                                        <li key={index} className="mb-2" style={{ lineHeight: '1.6' }}>
                                          {formatText(source)}
                                        </li>
                                      ))}
                                    </ol>
                                  </Card.Body>
                                </Card>
                              </div>
                            </Collapse>
                          </div>
                        )}

                        {/* Reasoning Section (Collapsible) */}
                        {parsed.reasoning && (
                          <div className="mb-3">
                            <Button
                              onClick={() => setReasoningOpen(!reasoningOpen)}
                              aria-controls="reasoning-collapse"
                              aria-expanded={reasoningOpen}
                              variant="outline-secondary"
                              className="w-100 text-start d-flex justify-content-between align-items-center"
                            >
                              <span><strong>🧠 Reasoning</strong></span>
                              <span>{reasoningOpen ? '▼' : '▶'}</span>
                            </Button>
                            <Collapse in={reasoningOpen}>
                              <div id="reasoning-collapse" className="mt-3">
                                <Card className="bg-light">
                                  <Card.Body>
                                    <div className="reasoning-content">
                                      {formatText(parsed.reasoning)}
                                    </div>
                                  </Card.Body>
                                </Card>
                              </div>
                            </Collapse>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="validation-content">
                        {formatText(results.validation || results.message || 'Validation completed successfully.')}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          );
        })()}
      </Container>
    </div>
  );
}

export default ValidatorPage;
